(function () {
  'use strict';

  const { gsap } = window;
  const { iman, BaseCarousel } = window.Universe;

  /* -- BoldSlideshow ------------------------------------------------- */

  class BoldSlideshow {
    constructor(section) {
      if (!section) return;

      this.onPrev = () => {
        this.instance.instance.scrollPrev();
      };

      this.onNext = () => {
        this.instance.instance.scrollNext();
      };

      this.onKeyDown = (e) => {
        if (!this.DOM.section.contains(document.activeElement)) return;

        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.isKeyboardInteraction = true;
            this.onPrev();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.isKeyboardInteraction = true;
            this.onNext();
            break;
        }
      };

      this.onChangeSlide = () => {
        const prevIndicator = this.DOM.indicators[this.activeIndex];
        const prevIndicatorInner = prevIndicator.querySelector('.js-bold-slideshow-indicator-inner');
        if (this.progressTl) {
          this.progressTl.kill();
        }

        if (this.autoPlay) {
          gsap.killTweensOf(prevIndicatorInner);

          gsap.to(prevIndicatorInner, {
            duration: 0.3,
            scaleX: 1,
            opacity: 0,
            ease: 'linear',
            onComplete: () => {
              prevIndicatorInner.style.transform = 'scaleX(0)';
              prevIndicatorInner.style.opacity = '1';
            },
          });

          this.stopAutoPlay();
        }

        const previousIndex = this.instance.instance.previousScrollSnap();
        const nextIndex = this.instance.instance.selectedScrollSnap();

        if (this.progressTl) {
          this.progressTl.kill();
        }

        const prevInner = this.DOM.contentSlideInners[previousIndex];
        const nextInner = this.DOM.contentSlideInners[nextIndex];

        gsap.killTweensOf([
          this.DOM.contentSlides[previousIndex],
          this.DOM.contentSlides[nextIndex],
          prevInner,
          nextInner,
        ]);
        // autoAlpha (opacity/visibility) stays on the positioned slide; the y transform
        // lives on the inner wrapper so it never clobbers the slide's centering transforms.
        gsap.to(this.DOM.contentSlides[previousIndex], { autoAlpha: 0, duration: 0.4 });
        gsap.to(prevInner, { y: 25, duration: 0.4 });
        gsap.to(this.DOM.contentSlides[nextIndex], { autoAlpha: 1, duration: 0.6, delay: 0.4 });
        gsap.to(nextInner, { y: 0, duration: 0.6, delay: 0.4 });

        this.activeIndex = nextIndex;

        if (this.autoPlay) {
          this.setupAutoPlayProgress();
          const activeIndicator = this.DOM.indicators[this.activeIndex];
          const activeIndicatorInner = activeIndicator.querySelector('.js-bold-slideshow-indicator-inner');
          gsap.killTweensOf(activeIndicatorInner);

          this.progressTl.to(activeIndicatorInner, {
            duration: this.autoplayDelay,
            ease: 'linear',
            scaleX: 1,
          });
        }

        this.updateIndicators();
        this.announceSlideChange();

        if (this.isKeyboardInteraction) {
          this.focusActiveSlide();
          this.isKeyboardInteraction = false;
        }

        if (this.progressTl) this.progressTl.play();
      };

      this.init(section);
    }

    setup() {
      this.autoPlay = 'autoplay' in this.DOM.section.dataset;
      this.autoplayDelay = parseFloat(this.DOM.section.dataset.autoplayDelay) || 3;

      this.instance = new BaseCarousel({
        wrap: this.DOM.section,
        carouselOptions: {
          containScroll: 'trimSnaps',
          loop: true,
          breakpoints: {
            '(min-width: 1024px)': {
              align: 'start',
              watchDrag: true,
            },
            '(max-width: 1023px)': {
              watchDrag: true,
            },
          },
        },
        autoPlayOnInit: false,
        withAutoPlay: this.autoPlay,
        autoPlayDelay: this.autoplayDelay * 1000,
        onReady: () => {
          this.setupContent();
          this.updateIndicators();
          this.setupAccessibility();

          this.instance.instance.on('select', this.onChangeSlide);

          if (this.DOM.prev.length) {
            for (let i = 0; i < this.DOM.prev.length; i++) {
              this.DOM.prev[i].addEventListener('click', this.onPrev);
            }
          }

          if (this.DOM.next.length) {
            for (let i = 0; i < this.DOM.next.length; i++) {
              this.DOM.next[i].addEventListener('click', this.onNext);
            }
          }

          // Keyboard navigation
          this.DOM.section.addEventListener('keydown', this.onKeyDown);

          if (this.autoPlay) {
            this.setupAutoPlayProgress();
            this.startAutoPlay();

            const activeIndicator = this.DOM.indicators[this.activeIndex];
            const activeIndicatorInner = activeIndicator.querySelector('.js-bold-slideshow-indicator-inner');
            this.progressTl.to(activeIndicatorInner, {
              duration: this.autoplayDelay,
              ease: 'linear',
              scaleX: 1,
            });
            this.progressTl.play();
          }
        },
      });
    }

    setupContent() {
      // Initial hidden state handled by CSS via sibling selector
    }

    updateIndicators() {
      for (let i = 0; i < this.DOM.indicators.length; i++) {
        this.DOM.indicators[i].classList.toggle('is-active', i === this.activeIndex);
      }
    }

    focusActiveSlide() {
      const activeSlide = this.DOM.contentSlides[this.activeIndex];
      if (!activeSlide) return;

      const focusable = activeSlide.querySelector('a, button, [tabindex="0"]');
      if (focusable) {
        focusable.focus();
      } else if (activeSlide.tagName === 'A' || activeSlide.hasAttribute('tabindex')) {
        activeSlide.focus();
      }
    }

    setupAccessibility() {
      this.slideLabel = this.DOM.section.dataset.slideLabel || 'Slide [current] of [total]';
      this.slideLabelWithTitle = this.DOM.section.dataset.slideLabelWithTitle || 'Slide [current] of [total]: [title]';

      this.DOM.announcer = document.createElement('div');
      this.DOM.announcer.setAttribute('aria-live', 'polite');
      this.DOM.announcer.setAttribute('aria-atomic', 'true');
      this.DOM.announcer.className = 'sr-only';
      this.DOM.announcer.style.cssText =
        'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;';
      this.DOM.section.appendChild(this.DOM.announcer);
    }

    announceSlideChange() {
      if (!this.DOM.announcer) return;

      const currentSlide = this.activeIndex + 1;
      const totalSlides = this.DOM.contentSlides.length;
      const currentSlideContent = this.DOM.contentSlides[this.activeIndex];

      let slideTitle = '';
      const heading = currentSlideContent.querySelector('.bold-slideshow__content-title');
      if (heading) {
        slideTitle = heading.textContent.trim();
      }

      const announcement = slideTitle
        ? this.slideLabelWithTitle.replace('[current]', currentSlide).replace('[total]', totalSlides).replace('[title]', slideTitle)
        : this.slideLabel.replace('[current]', currentSlide).replace('[total]', totalSlides);

      this.DOM.announcer.textContent = announcement;
    }

    setupAutoPlayProgress() {
      this.progressTl = gsap.timeline({
        paused: true,
      });
    }

    stopAutoPlay() {
      const emblaInstance = this.instance && this.instance.instance;
      const plugins = emblaInstance && emblaInstance.plugins && emblaInstance.plugins();
      const autoplay = plugins && plugins.autoplay;
      if (autoplay) {
        autoplay.stop();
      }
    }

    startAutoPlay() {
      const emblaInstance = this.instance && this.instance.instance;
      const plugins = emblaInstance && emblaInstance.plugins && emblaInstance.plugins();
      const autoplay = plugins && plugins.autoplay;
      if (autoplay) {
        autoplay.play();
      }
    }

    destroy() {
      if (this.DOM) {
        if (this.DOM.prev.length) {
          for (let i = 0; i < this.DOM.prev.length; i++) {
            this.DOM.prev[i].removeEventListener('click', this.onPrev);
          }
        }

        if (this.DOM.next.length) {
          for (let i = 0; i < this.DOM.next.length; i++) {
            this.DOM.next[i].removeEventListener('click', this.onNext);
          }
        }

        // Remove keyboard navigation
        this.DOM.section.removeEventListener('keydown', this.onKeyDown);

        // Remove announcer
        if (this.DOM.announcer) {
          this.DOM.announcer.remove();
        }

        if (this.instance && this.instance.instance) {
          this.instance.instance.off('select', this.onChangeSlide);
        }
        this.instance.destroy();

        this.instance = undefined;
        this.DOM = undefined;
      }
    }

    init(section) {
      this.DOM = {};
      this.DOM.section = section;
      this.sectionId = section.closest('.shopify-section')?.id || null;
      this.DOM.indicators = this.DOM.section.querySelectorAll('.js-bold-slideshow-indicator');
      this.DOM.indicatorsInner = this.DOM.section.querySelectorAll('.js-bold-slideshow-indicator-inner');
      this.DOM.prev = this.DOM.section.querySelectorAll('.js-carousel-prev');
      this.DOM.next = this.DOM.section.querySelectorAll('.js-carousel-next');
      this.DOM.contentCarousel = this.DOM.section.querySelector('.js-bold-slideshow-content-carousel');
      this.DOM.contentSlides = this.DOM.section.querySelectorAll('.js-bold-slideshow-content-slide');
      this.DOM.contentSlideInners = this.DOM.section.querySelectorAll('.js-bold-slideshow-content-slide-inner');

      this.activeIndex = 0;
      this.isKeyboardInteraction = false;

      this.setup();
    }
  }

  /* -- BoldSlideshows ------------------------------------------------ */

  class BoldSlideshows {
    constructor() {
      this.init();
    }

    setup() {
      for (let i = 0; i < this.DOM.sections.length; i++) {
        this.carousels.push(new BoldSlideshow(this.DOM.sections[i]));
      }
    }

    reinit() {
      this.destroy();
      this.init();
    }

    reinitSection(sectionEl) {
      this.destroySection(sectionEl);

      if (!this.DOM) {
        this.DOM = {};
        this.carousels = [];
      }

      const newSections = sectionEl.querySelectorAll('.js-bold-slideshow-section');
      for (let i = 0; i < newSections.length; i++) {
        this.carousels.push(new BoldSlideshow(newSections[i]));
      }

      this.DOM.sections = document.querySelectorAll('.js-bold-slideshow-section');
    }

    destroySection(sectionEl) {
      if (!this.carousels) return;

      const targetId = sectionEl.id || sectionEl.closest('.shopify-section')?.id;
      if (!targetId) return;

      for (let i = this.carousels.length - 1; i >= 0; i--) {
        const carousel = this.carousels[i];
        if (carousel.sectionId === targetId) {
          carousel.destroy();
          this.carousels.splice(i, 1);
        }
      }
    }

    onBlockSelect(blockEl) {
      for (let i = 0; i < this.carousels.length; i++) {
        const carousel = this.carousels[i];
        if (carousel.DOM && carousel.DOM.section.contains(blockEl)) {
          const slide = blockEl.closest('.js-bold-slideshow-slide');
          if (!slide) break;

          const slides = carousel.DOM.section.querySelectorAll('.js-bold-slideshow-slide');
          const slideIndex = Array.from(slides).indexOf(slide);
          if (slideIndex !== -1 && carousel.instance && carousel.instance.instance) {
            const autoplay = carousel.instance.instance.plugins().autoplay;
            if (autoplay) autoplay.stop();
            carousel.instance.instance.scrollTo(slideIndex);
          }
          break;
        }
      }
    }

    onBlockDeselect(blockEl) {
      for (let i = 0; i < this.carousels.length; i++) {
        const carousel = this.carousels[i];
        if (carousel.DOM && carousel.DOM.section.contains(blockEl)) {
          if (carousel.instance && carousel.instance.instance) {
            const autoplay = carousel.instance.instance.plugins().autoplay;
            if (autoplay) autoplay.play();
          }
          break;
        }
      }
    }

    destroy() {
      if (this.DOM) {
        for (let i = 0; i < this.carousels.length; i++) {
          this.carousels[i].destroy();
        }
        this.carousels = [];
        this.DOM = undefined;
      }
    }

    init(container = document) {
      const sections = container.querySelectorAll('.js-bold-slideshow-section');

      if (sections.length) {
        this.DOM = {};
        this.DOM.sections = sections;

        this.carousels = [];

        this.setup();
      }
    }
  }

  /* -- Init ---------------------------------------------------------- */

  function init() {
    if (!iman.get('boldSlideshows')) {
      iman.add('boldSlideshows', new BoldSlideshows());
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Universe.BoldSlideshows = BoldSlideshows;
})();
